import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { encodeRef, libraryRef } from '@shared/services/imageRefs'
import { MIRO_FONTS, loadAllMiroFonts, loadMiroFont, miroFont, type MiroFontKey, type TextStyle } from '@shared/data/miroFonts'
import {
  FOOT,
  FOOT_MAX,
  FOOT_MIN,
  GAP,
  HALF_MAX,
  HALF_MIN,
  LAYOUTS,
  MARGIN,
  SHEET_H,
  SHEET_W,
  coverCrop,
  footParts,
  placePictures,
  type Crop,
  type LayoutKind,
  type Rect,
} from '@shared/moodboard/layout'
import { boardPalette, orderByColour, readColour, type PictureColour } from '@shared/moodboard/colour'
import {
  GUTTER_MAX,
  GUTTER_STOPS,
  nodeAt,
  placeBySplits,
  smallest,
  treeOf,
  type Divider,
  type SplitTree,
  type Splits,
} from '@shared/moodboard/splits'
import { keptShare, normalAngle, splitTurn, turnedRatio } from '@shared/moodboard/turn'
import type { SentMoodboard, SentPicture } from '@shared/moodboard/send'
import { alphaKind, drawOutlined, flattenAlpha, floatBounds, keepOnSheet, type FloatSpec } from '@shared/moodboard/float'
import type { SourcePicture } from '../pages/MoodboardStart'
import { bitmapOf } from '../pages/moodboardRender'
import '../pages/moodboard.css'

/**
 * The mood board sheet and its controls (Huanrui, 2026-10-16)
 * -------------------------------------------------------------
 * Taken out of the Mood board page so the canvas's Mood board card can open
 * the same thing full screen. Everything that is done ON the sheet lives
 * here: the layout, the ground, the foot strip with its title, words and
 * colours, dragging one picture onto another (they swap frames), the
 * double-click re-cut (drag it about, drag its corners or the wheel to size
 * it, the rest darkened), Delete to take a clicked picture off, a click on a
 * text to change its words and type.
 *
 * What is done WITH the sheet is the caller's: the page sends it to Miro or
 * copies it to a project; the canvas card makes a frame of it beside itself.
 * They are given the sheet through `footer`, drawn at the foot of the side
 * column.
 *
 * The swap button in a picture's corner shows only when the caller can find
 * more pictures (`findMore`): the page can (it searched for them), the card
 * cannot (its pictures are the frame's, Huanrui 2026-10-16).
 *
 * The settings (BoardSettings) are by picture INDEX into `pictures`; the
 * card keeps them by picture key (MoodboardStudio.tsx) so they survive the
 * frame's pictures changing.
 */

/** More than this and nothing on the sheet is big enough to read. */
export const MOST_PICTURES = 16
/** How long a picture may take to report its colour before the board goes ahead without it. */
export const COLOUR_WAIT_MS = 6000

export const GROUNDS: { label: string; color: string }[] = [
  { label: 'Paper', color: '#f5f2ed' },
  { label: 'White', color: '#ffffff' },
  { label: 'Stone', color: '#e4e1dc' },
  { label: 'Black', color: '#111111' },
]

export const CREDIT_STYLE: TextStyle = { font: 'plex_sans', size: 10, bold: false, align: 'left', color: '#9a9a9a' }

export const isDark = (hex: string) => {
  const v = parseInt(hex.slice(1), 16)
  return 0.299 * (v >> 16) + 0.587 * ((v >> 8) & 255) + 0.114 * (v & 255) < 128
}

export interface BoardPicture {
  src: SourcePicture
  ratio: number
  colour: PictureColour | null
  /**
   * See-through parts (2026-10-23; float.ts alphaKind): 'cutout', shown from
   * its own file so they show; 'semi', a translucent ground, shown from a
   * solid copy (src.url and src.thumb; src.full the file itself) and sent
   * solid. Unset: none, or not looked at.
   */
  clear?: 'cutout' | 'semi'
}

type Corner = 'nw' | 'ne' | 'sw' | 'se'

type Selected =
  | { kind: 'picture'; index: number }
  | { kind: 'text'; id: 'title' | 'keywords' }
  // A lifted picture, by which picture it is (its place in the list changes as it is brought to the top).
  | { kind: 'float'; pic: number }
  | null

/**
 * A picture lifted off the grid (Huanrui, 2026-10-23; float.ts): its middle
 * on the sheet, its width (its height follows from its shape) and its turn,
 * in degrees. Over everything; the last one on top.
 */
export interface BoardFloat {
  pic: number
  x: number
  y: number
  w: number
  angle: number
  /**
   * The outline's width in sheet units, chosen on its own (Huanrui,
   * 2026-10-23); none: as wide as the gutters, following them.
   */
  outline?: number
}

/** Everything about a sheet that can be changed, by picture index. */
export interface BoardSettings {
  /** Which pictures are on the sheet, in order. Indexes into the pictures. */
  order: number[]
  kind: LayoutKind
  ground: string
  foot: boolean
  showColours: boolean
  title: string
  keywords: string
  titleStyle: TextStyle
  wordsStyle: TextStyle
  /**
   * The order the frames were laid out for. Dragging a picture onto another
   * swaps the two pictures but keeps every frame where it is
   * (2026-10-12: "不影响图片frame的情况拖拽图片来更换图片位置"); anything else
   * that changes the pictures lays the sheet out afresh (null).
   */
  frozen: number[] | null
  /**
   * Where each picture is cut, by picture: 0..1 along the part that does not
   * fit, and how far it is zoomed in (1 = just filling its frame). Unset: the
   * usual cut.
   */
  focus: Record<number, { x: number; y: number; z?: number }>
  /**
   * The gutter between frames, in sheet units, every one the same
   * (2026-10-16). GAP unless changed.
   */
  gutter: number
  /**
   * Frames sized by dragging the gutters between them (2026-10-16): each
   * node's shares, by path (shared/moodboard/splits.ts). Empty: as laid out.
   */
  splits: Splits
  /** Each picture's turn in degrees, by picture (2026-10-16): turned in its frame while re-cutting. */
  turns: Record<number, number>
  /** The title, words and colours: along the foot or across the top, and how tall (2026-10-16). */
  footAt: 'top' | 'bottom'
  footH: number
  /**
   * How far the pictures keep from the sheet's edges, 0 to MARGIN (Huanrui,
   * 2026-10-23); the title strip and colours stay where they are. Unset on a
   * sheet kept before: MARGIN, as it was.
   */
  margin?: number
  /** Pictures lifted off the grid (2026-10-23). None on a sheet kept before. */
  floats?: BoardFloat[]
  /** Half: the share of the width the pictures take, dragged from their left edge (2026-10-16). Null: half. */
  half: number | null
}

export const DEFAULT_TITLE_STYLE: TextStyle = { font: 'eb_garamond', size: 34, bold: false, align: 'left', color: '#1c1c1c' }
export const DEFAULT_WORDS_STYLE: TextStyle = { font: 'plex_sans', size: 13, bold: false, align: 'left', color: '#6f6f6f' }

/** A new sheet: the pictures in `order`, everything else as the page has always started. */
export function freshSettings(order: number[], words: { title?: string; keywords?: string } = {}): BoardSettings {
  return {
    order,
    kind: 'editorial',
    // White to begin with (Huanrui, 2026-10-23; it was Paper).
    ground: '#ffffff',
    // Pictures only, to begin with (Huanrui, 2026-10-23): the title and words
    // and the colour strip are there to be ticked.
    foot: false,
    showColours: false,
    title: words.title?.trim() || 'Mood board',
    keywords: words.keywords ?? '',
    titleStyle: DEFAULT_TITLE_STYLE,
    wordsStyle: DEFAULT_WORDS_STYLE,
    frozen: null,
    focus: {},
    gutter: GAP,
    margin: MARGIN,
    floats: [],
    splits: {},
    turns: {},
    footAt: 'bottom',
    footH: FOOT,
    half: null,
  }
}

export function measure(src: SourcePicture): Promise<number> {
  if (src.width > 0 && src.height > 0) return Promise.resolve(src.width / src.height)
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image.naturalWidth > 0 ? image.naturalWidth / image.naturalHeight : 1)
    image.onerror = () => resolve(1)
    image.src = src.thumb
  })
}

export function withTimeout<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([work, new Promise<T>((resolve) => window.setTimeout(() => resolve(fallback), ms))])
}

/** One picture's shape and colours, read. */
export async function readPicture(src: SourcePicture): Promise<BoardPicture> {
  const [ratio, colour, clear] = await Promise.all([
    measure(src),
    withTimeout(readColour(src.thumb), COLOUR_WAIT_MS, null),
    withTimeout(seeThrough(src), CLEAR_WAIT_MS, null),
  ])
  if (clear?.kind === 'semi') return { src: { ...src, full: src.full || src.url, url: clear.solid, thumb: clear.solid }, ratio, colour, clear: 'semi' }
  return { src, ratio, colour, ...(clear?.kind === 'cutout' ? { clear: 'cutout' as const } : {}) }
}

/** How long a picture's see-through parts may take to be looked at before it is shown as it is. */
const CLEAR_WAIT_MS = 8000

/**
 * A picture that may be see-through (PNG, WebP, GIF, or a kind not known),
 * looked at once, from its own file (2026-10-23): a cut-out, or a
 * translucent ground -- with a solid copy of it to show (float.ts).
 */
async function seeThrough(src: SourcePicture): Promise<{ kind: 'cutout' } | { kind: 'semi'; solid: string } | null> {
  if (!mayBeClear(src)) return null
  const image = await bitmapOf(src.url)
  try {
    const kind = alphaKind(image)
    if (kind === 'cutout') return { kind }
    if (kind !== 'semi') return null
    const copy = flattenAlpha(image, 2400)
    const blob = await new Promise<Blob | null>((resolve) => copy.toBlob(resolve, 'image/jpeg', 0.92))
    return blob ? { kind, solid: URL.createObjectURL(blob) } : null
  } finally {
    image.close()
  }
}

/** The pictures read, and the order that flows by colour (the first MOST_PICTURES). */
export async function readPictures(sources: SourcePicture[]): Promise<{ pictures: BoardPicture[]; order: number[] }> {
  const pictures = await Promise.all(sources.map(readPicture))
  const flowing = orderByColour(
    pictures.map((_, i) => i),
    (i) => pictures[i].colour,
  )
  return { pictures, order: flowing.slice(0, MOST_PICTURES) }
}

/** "Photos: Unsplash — A, B · Pexels — C" for whatever from them is on the sheet. */
export function credits(pictures: SourcePicture[]): string {
  const by = (source: 'unsplash' | 'pexels') => [
    ...new Set(pictures.filter((one) => one.source === source && one.credit).map((one) => one.credit!.author)),
  ]
  const parts = (['unsplash', 'pexels'] as const)
    .map((source) => ({ source, names: by(source) }))
    .filter((one) => one.names.length > 0)
    .map((one) => `${one.source === 'unsplash' ? 'Unsplash' : 'Pexels'} — ${one.names.join(', ')}`)
  return parts.length > 0 ? `Photos: ${parts.join(' · ')}` : ''
}

/** Unsplash asks for these on every link back to it. */
const UNSPLASH_HOME = 'https://unsplash.com/?utm_source=wonderworks&utm_medium=referral'

/**
 * The credit line, on screen, with each photographer linked to their page
 * and Unsplash / Pexels to theirs (Huanrui, 2026-10-20: Unsplash's review
 * asks to see the linked credit wherever its photos show). The same words as
 * credits(); what goes to Miro or into a picture keeps the plain line, where
 * a link cannot be followed. A photographer without a page is named only.
 */
function CreditLinks({ sources, fallback }: { sources: SourcePicture[]; fallback: string }) {
  const groups = (['unsplash', 'pexels'] as const)
    .map((source) => {
      const seen = new Map<string, string>()
      for (const one of sources) {
        if (one.source === source && one.credit && !seen.has(one.credit.author)) seen.set(one.credit.author, one.credit.authorUrl)
      }
      return { source, people: [...seen.entries()] }
    })
    .filter((one) => one.people.length > 0)
  if (groups.length === 0) return <>{fallback}</>
  const stop = (event: React.SyntheticEvent) => event.stopPropagation()
  const link = (href: string, text: string) =>
    href ? (
      <a href={href} target="_blank" rel="noreferrer noopener" onPointerDown={stop} onClick={stop}>
        {text}
      </a>
    ) : (
      <span>{text}</span>
    )
  return (
    <>
      Photos:{' '}
      {groups.map((group, g) => (
        <span key={group.source}>
          {g > 0 ? ' · ' : null}
          {group.source === 'unsplash' ? link(UNSPLASH_HOME, 'Unsplash') : link('https://www.pexels.com', 'Pexels')}
          {' — '}
          {group.people.map(([name, href], i) => (
            <span key={name}>
              {i > 0 ? ', ' : null}
              {link(href, name)}
            </span>
          ))}
        </span>
      ))}
    </>
  )
}

export interface SheetSlot extends Rect {
  pic: number
  crop: Crop
  /** Degrees clockwise; the crop is of the picture turned (turn.ts). */
  turn?: number
}

/** Shown whole in its frame: nothing to cut, so the original itself can go. */
export const shownWhole = (crop: Crop): boolean =>
  crop.x < 0.002 && crop.y < 0.002 && crop.w > 0.996 && crop.h > 0.996
/** A lifted picture as drawn: where its middle is, how it is drawn, and the box that takes. */
export interface SheetFloat {
  pic: number
  x: number
  y: number
  spec: FloatSpec
  box: Rect
}

export interface Sheet {
  slots: SheetSlot[]
  /** The lifted pictures, the last on top (2026-10-23). */
  floats: SheetFloat[]
  parts: ReturnType<typeof footParts> | null
  credit: string
  /** How the frames were cut, and the gutters that can be dragged (splits.ts); null when none can. */
  tree: SplitTree | null
  dividers: Divider[]
}

/** The colours of the strip: the five that the pictures on the sheet share most. */
export function paletteOf(pictures: BoardPicture[], order: number[]): string[] {
  return boardPalette(order.map((i) => pictures[i]?.colour), 5)
}

/** Where everything goes on the 1920 x 1080 sheet. */
export function layOut(pictures: BoardPicture[], s: BoardSettings, palette: string[]): Sheet {
  const order = s.order.filter((i) => pictures[i])
  const basis = s.frozen && s.frozen.length === order.length ? s.frozen : order
  // Laid out by the pictures' own shapes: turning one turns it in its frame, the frames stay.
  const laid = placePictures(s.kind, {
    ratios: basis.map((i) => pictures[i]?.ratio ?? 1),
    foot: s.foot,
    footAt: s.footAt,
    footH: s.footH,
    margin: s.margin ?? MARGIN,
    ...(s.half !== null && s.half !== undefined ? { half: s.half } : {}),
  })
  // Cut again by the same rows and columns, at the gutter and the sizes chosen.
  const tree = treeOf(laid)
  const placed = tree ? placeBySplits(laid, tree, s.gutter ?? GAP, s.splits ?? {}) : { rects: laid, dividers: [] }
  const rects = placed.rects
  const slots = rects.map((rect, n) => {
    const pic = order[n]
    const turn = normalAngle(s.turns?.[pic])
    const crop = coverCrop(turnedRatio(pictures[pic].ratio, turn), rect.w, rect.h)
    const at = s.focus[pic]
    if (at) {
      const z = Math.max(1, at.z ?? 1)
      crop.w /= z
      crop.h /= z
      crop.x = (1 - crop.w) * at.x
      crop.y = (1 - crop.h) * at.y
    }
    return { ...rect, pic, crop, ...(turn ? { turn } : {}) }
  })
  const parts = s.foot ? footParts(s.showColours ? palette.length : 0, s.footAt, s.footH) : null
  // The lifted pictures: whole, outlined in the ground's colour, as wide as
  // the gutter unless a width of its own was chosen.
  const floats: SheetFloat[] = (s.floats ?? [])
    .filter((one) => pictures[one.pic] && !order.includes(one.pic))
    .map((one) => {
      const spec: FloatSpec = {
        angle: one.angle,
        outline: one.outline ?? s.gutter ?? GAP,
        color: s.ground,
        w: one.w,
        h: one.w / (pictures[one.pic].ratio || 1),
      }
      // Never off the sheet, whatever moved it there (a wider gutter, an older save).
      const at = keepOnSheet(one.x, one.y, spec, SHEET_W, SHEET_H)
      return { pic: one.pic, x: at.x, y: at.y, spec, box: floatBounds(at.x, at.y, spec) }
    })
  const credit = credits([...order, ...floats.map((one) => one.pic)].map((i) => pictures[i].src))
  return { slots, floats, parts, credit, tree, dividers: placed.dividers }
}

/** The sheet as it goes to Miro, or to a frame on a project's board. */
export function buildBoard(pictures: BoardPicture[], sheet: Sheet, s: BoardSettings, palette: string[]): SentMoodboard {
  const texts: SentMoodboard['texts'] = []
  if (sheet.parts) {
    if (s.title.trim()) texts.push({ text: s.title.trim(), ...sheet.parts.title, style: s.titleStyle })
    if (s.keywords.trim()) texts.push({ text: s.keywords.trim(), ...sheet.parts.keywords, style: s.wordsStyle })
  }
  // The credit line stays on screen only, linked (CreditLinks); it is not put on the board (Huanrui, 2026-10-20).
  return {
    title: s.title.trim() || 'Mood board',
    background: s.ground,
    pictures: sheet.slots.map((slot) => {
      const src = pictures[slot.pic].src
      return {
        ...sentSource(src),
        x: slot.x,
        y: slot.y,
        w: slot.w,
        h: slot.h,
        crop: slot.crop,
        ...(slot.turn ? { turn: slot.turn } : {}),
      }
    }),
    // Lifted pictures (2026-10-23): whole, drawn with their outline and turn
    // into the box they take, over the rest.
    ...(sheet.floats.length > 0
      ? {
          floats: sheet.floats.map((one) => ({
            ...sentSource(pictures[one.pic].src),
            ...one.box,
            crop: { x: 0, y: 0, w: 1, h: 1 },
            float: one.spec,
          })),
        }
      : {}),
    texts,
    swatches: sheet.parts ? sheet.parts.swatches.map((rect, i) => ({ ...rect, color: palette[i] })) : [],
  }
}

/** Which picture, for Miro and a project's board: its reference, or its address, and its name with its credit. */
function sentSource(src: SourcePicture): Pick<SentPicture, 'ref' | 'url' | 'title'> {
  return {
    ref: src.asset ? encodeRef(libraryRef(src.asset.id)) : src.ref ?? '',
    // Unsplash, Pexels: the whole picture, not the size shown (2026-10-16).
    url: src.asset || src.ref ? undefined : src.full || src.url,
    title: src.credit ? `${src.title} · ${src.credit.author} (${src.source === 'unsplash' ? 'Unsplash' : 'Pexels'})` : src.title,
  }
}

/** What the caller is handed to act on the sheet. */
export interface BoardApi {
  pictures: BoardPicture[]
  settings: BoardSettings
  sheet: Sheet
  /** The sheet as it goes to Miro or to a frame. */
  board: () => SentMoodboard
}

export interface MoodboardBoardProps {
  pictures: BoardPicture[]
  initial: BoardSettings
  /** Every change, for a caller that keeps the settings (the canvas card). */
  onChange?: (settings: BoardSettings, pictures: BoardPicture[]) => void
  /**
   * More pictures like these, for the swap button once the spares are used
   * up: one of a shape close to `shape`, none of `used`. Throws to say why
   * there is none. Absent: no swap button at all.
   */
  findMore?: (want: { shape: number; was: BoardPicture; used: Set<string> }) => Promise<SourcePicture>
  /** Added at the end of the hint under the sheet. */
  hint?: ReactNode
  /** The foot of the side column: what is done with the sheet. */
  footer: (api: BoardApi) => ReactNode
  className?: string
}

export function MoodboardBoard({ pictures: given, initial, onChange, findMore, hint, footer, className }: MoodboardBoardProps) {
  const [pictures, setPictures] = useState<BoardPicture[]>(given)
  const [order, setOrder] = useState<number[]>(initial.order)
  const [kind, setKind] = useState<LayoutKind>(initial.kind)
  const [ground, setGround] = useState(initial.ground)
  const [foot, setFoot] = useState(initial.foot)
  const [showColours, setShowColours] = useState(initial.showColours)
  const [title, setTitle] = useState(initial.title)
  const [keywords, setKeywords] = useState(initial.keywords)
  const [titleStyle, setTitleStyle] = useState<TextStyle>(initial.titleStyle)
  const [wordsStyle, setWordsStyle] = useState<TextStyle>(initial.wordsStyle)
  const [selected, setSelected] = useState<Selected>(null)
  const [frozen, setFrozen] = useState<number[] | null>(initial.frozen)
  const [focus, setFocus] = useState<Record<number, { x: number; y: number; z?: number }>>(initial.focus)
  const [gutter, setGutter] = useState<number>(initial.gutter ?? GAP)
  const [margin, setMargin] = useState<number>(initial.margin ?? MARGIN)
  const [floats, setFloats] = useState<BoardFloat[]>(initial.floats ?? [])
  const [splits, setSplits] = useState<Splits>(initial.splits ?? {})
  const [turns, setTurns] = useState<Record<number, number>>(initial.turns ?? {})
  const [footAt, setFootAt] = useState<'top' | 'bottom'>(initial.footAt ?? 'bottom')
  const [footH, setFootH] = useState<number>(initial.footH ?? FOOT)
  const [half, setHalf] = useState<number | null>(initial.half ?? null)
  /** A gutter being dragged, and the line it has settled on (splits.ts). */
  const [sizing, setSizing] = useState<{ path: string; index: number } | null>(null)
  const [guide, setGuide] = useState<{ axis: 'x' | 'y'; at: number; why: string } | null>(null)
  /** The frame being re-cut (double-click), by slot. */
  const [cropping, setCropping] = useState<number | null>(null)
  /** A picture being dragged to another frame. */
  const [drag, setDrag] = useState<{ from: number; over: number | null; x: number; y: number } | null>(null)
  const [error, setError] = useState('')

  const settings = useMemo<BoardSettings>(
    () => ({ order, kind, ground, foot, showColours, title, keywords, titleStyle, wordsStyle, frozen, focus, gutter, margin, floats, splits, turns, footAt, footH, half }),
    [order, kind, ground, foot, showColours, title, keywords, titleStyle, wordsStyle, frozen, focus, gutter, margin, floats, splits, turns, footAt, footH, half],
  )
  const told = useRef(onChange)
  told.current = onChange
  useEffect(() => {
    told.current?.(settings, pictures)
  }, [settings, pictures])

  useEffect(() => {
    loadMiroFont(titleStyle.font)
    loadMiroFont(wordsStyle.font)
    loadMiroFont(CREDIT_STYLE.font)
  }, [titleStyle.font, wordsStyle.font])

  // Dark ground: light words.
  const setGroundAndText = (color: string) => {
    const dark = isDark(color)
    if (dark !== isDark(ground)) {
      setTitleStyle((s) => ({ ...s, color: dark ? '#f2f2f2' : '#1c1c1c' }))
      setWordsStyle((s) => ({ ...s, color: dark ? '#a8a8a8' : '#6f6f6f' }))
    }
    setGround(color)
  }


  // ---------------------------------------------------------- the sheet
  const palette = useMemo(() => paletteOf(pictures, order), [pictures, order])
  const sheet = useMemo(() => layOut(pictures, settings, palette), [pictures, settings, palette])
  // A lifted picture is on the sheet, not a spare.
  const spares = useMemo(
    () => pictures.map((_, i) => i).filter((i) => !order.includes(i) && !floats.some((one) => one.pic === i)),
    [pictures, order, floats],
  )
  /** A picture's shape as it shows: turned, if it has been. */
  const shapeOf = (pic: number) => turnedRatio(pictures[pic]?.ratio || 1, turns[pic])

  // The sheet is drawn at whatever width it is given; everything on it
  // scales from 1920 sheet units.
  const stage = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(0.5)
  /*
   * The sheet's width on screen, in whole pixels and a multiple of 16, so its
   * height (9/16 of it) is whole pixels too (Huanrui, 2026-10-23). It was
   * whatever the column gave -- a fraction of a pixel -- while the scale was
   * worked out from the rounded width: the pictures came out a fraction short
   * of the right and bottom edges, and a sliver of the ground showed there.
   * Now every edge on the sheet falls on a whole pixel. Null until measured.
   */
  const [sheetWidth, setSheetWidth] = useState<number | null>(null)
  useEffect(() => {
    const node = stage.current
    const column = node?.parentElement
    if (!node || !column) return
    const fit = (room: number) => {
      const width = Math.max(16, Math.floor(room / 16) * 16)
      setSheetWidth(width)
      setScale(width / SHEET_W)
    }
    const observer = new ResizeObserver((entries) => fit(entries[0]?.contentRect.width ?? column.clientWidth))
    observer.observe(column)
    fit(column.getBoundingClientRect().width - 2)
    return () => observer.disconnect()
  }, [])


  // ---------------------------------------------------------- edits
  const relayout = () => {
    setFrozen(null)
    setCropping(null)
    // Laid out afresh: the frames' sizes start again too.
    setSplits({})
  }

  /**
   * A gutter dragged (Huanrui, 2026-10-16): the frame before it and the one
   * after trade size, the gutter keeps its width, nothing else moves. It
   * settles near an even split, a frame the shape of its picture, another
   * gutter's line, or the middle of the sheet; Alt held, it does not settle.
   */
  const startDivider = (event: React.PointerEvent<HTMLElement>, d: Divider) => {
    if (event.button !== 0 || !sheet.tree || !stage.current) return
    event.preventDefault()
    event.stopPropagation()
    const node = nodeAt(sheet.tree, d.path)
    if (!node || typeof node === 'number') return
    const axis = d.axis
    const a = node.children[d.index]!
    const b = node.children[d.index + 1]!
    const startA = d.at - d.sizes[d.index]!
    const pair = d.sizes[d.index]! + d.sizes[d.index + 1]!
    const minA = smallest(a, axis, gutter)
    const minB = smallest(b, axis, gutter)
    if (pair < minA + minB) return
    const across = axis === 'x' ? d.region.h : d.region.w
    // A frame holding one picture: the size that shows it whole.
    const fitOf = (child: SplitTree) => {
      if (typeof child !== 'number') return null
      const slotPic = sheet.slots[child]?.pic
      if (slotPic === undefined) return null
      const ratio = shapeOf(slotPic)
      return axis === 'x' ? across * ratio : across / ratio
    }
    const candidates: { size: number; why: string }[] = [{ size: pair / 2, why: 'equal' }]
    const fitA = fitOf(a)
    if (fitA) candidates.push({ size: fitA, why: 'fit' })
    const fitB = fitOf(b)
    if (fitB) candidates.push({ size: pair - fitB, why: 'fit' })
    for (const other of sheet.dividers) {
      if (other.axis !== axis || (other.path === d.path && other.index === d.index)) continue
      candidates.push({ size: other.at - startA, why: 'line' })
    }
    const middle = (axis === 'x' ? SHEET_W : SHEET_H) / 2
    candidates.push({ size: middle - gutter / 2 - startA, why: 'centre' })

    const rect = stage.current.getBoundingClientRect()
    const grab = (axis === 'x' ? (event.clientX - rect.left) : (event.clientY - rect.top)) / scale - d.at
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)
    setSizing({ path: d.path, index: d.index })
    const onMove = (e: PointerEvent) => {
      const pointer = (axis === 'x' ? e.clientX - rect.left : e.clientY - rect.top) / scale - grab
      let size = pointer - startA
      let why = ''
      if (!e.altKey) {
        const reach = 8 / scale
        let best = reach
        for (const one of candidates) {
          const off = Math.abs(one.size - size)
          if (off < best && one.size >= minA && pair - one.size >= minB) {
            best = off
            size = one.size
            why = one.why
          }
        }
      }
      size = Math.min(pair - minB, Math.max(minA, size))
      setGuide(why ? { axis, at: startA + size + gutter / 2, why } : null)
      const free = d.sizes.reduce((x, y) => x + y, 0) || 1
      const sizes = [...d.sizes]
      sizes[d.index] = size
      sizes[d.index + 1] = pair - size
      setSplits((now) => ({ ...now, [d.path]: sizes.map((v) => v / free) }))
    }
    const onUp = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      setSizing(null)
      setGuide(null)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  /** A picture's turn set, in degrees: the frame stays, the picture is cut to it afresh. */
  const setTurn = (pic: number, degrees: number) =>
    setTurns((now) => {
      const next = normalAngle(degrees)
      const out = { ...now }
      if (next) out[pic] = next
      else delete out[pic]
      return out
    })

  /** The angle shown while the turn handle is held. */
  const [turning, setTurning] = useState<number | null>(null)

  /**
   * The turn handle while re-cutting (Huanrui, 2026-10-16: any angle, one
   * button): held and dragged round the frame's middle, the picture follows
   * to any angle, straightened as a photo editor does -- turned and filling
   * its frame, no corner left empty. It settles on the upright and the
   * quarters within 3 degrees; Shift steps by 15; Alt does not settle. A
   * click without dragging turns it a quarter.
   */
  const startTurn = (event: React.PointerEvent<HTMLElement>, n: number) => {
    if (event.button !== 0 || !stage.current) return
    event.preventDefault()
    event.stopPropagation()
    const slot = sheet.slots[n]
    if (!slot) return
    const pic = slot.pic
    const rect = stage.current.getBoundingClientRect()
    const cx = rect.left + (slot.x + slot.w / 2) * scale
    const cy = rect.top + (slot.y + slot.h / 2) * scale
    const angleAt = (x: number, y: number) => (Math.atan2(y - cy, x - cx) * 180) / Math.PI
    const from = angleAt(event.clientX, event.clientY)
    const start = turns[pic] ?? 0
    const x0 = event.clientX
    const y0 = event.clientY
    let dragged = false
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)
    const onMove = (e: PointerEvent) => {
      if (!dragged && Math.hypot(e.clientX - x0, e.clientY - y0) < 4) return
      dragged = true
      let angle = start + angleAt(e.clientX, e.clientY) - from
      if (e.shiftKey) angle = Math.round(angle / 15) * 15
      else if (!e.altKey) {
        const quarter = Math.round(angle / 90) * 90
        if (Math.abs(angle - quarter) < 3) angle = quarter
      }
      angle = Math.round(angle * 10) / 10
      setTurn(pic, angle)
      setTurning(normalAngle(angle))
    }
    const onUp = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      setTurning(null)
      // A click: a quarter turn, from the nearest quarter.
      if (!dragged) setTurn(pic, Math.round(start / 90) * 90 + 90)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  /**
   * Half: the pictures' left edge dragged (Huanrui, 2026-10-16), so they take
   * more or less of the sheet. It settles on a half, a third, two thirds and
   * the golden cut; Alt held, it does not settle.
   */
  const startHalfEdge = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !stage.current) return
    event.preventDefault()
    event.stopPropagation()
    const rect = stage.current.getBoundingClientRect()
    // The pictures' own margin (2026-10-23), which Half's edge is measured within.
    const width = SHEET_W - margin * 2
    const right = SHEET_W - margin
    const stops = [
      { share: (width - GAP) / 2 / width, why: 'Half' },
      { share: 1 / 3, why: 'A third' },
      { share: 2 / 3, why: 'Two thirds' },
      { share: 0.382, why: 'Golden' },
      { share: 0.618, why: 'Golden' },
    ]
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)
    setSizing({ path: 'half', index: 0 })
    const onMove = (e: PointerEvent) => {
      const x = (e.clientX - rect.left) / scale
      let share = (right - x) / width
      let why = ''
      if (!e.altKey) {
        let best = 8 / scale / width
        for (const stop of stops) {
          const off = Math.abs(stop.share - share)
          if (off < best) {
            best = off
            share = stop.share
            why = stop.why
          }
        }
      }
      share = Math.min(HALF_MAX, Math.max(HALF_MIN, share))
      setHalf(share)
      setGuide(why ? { axis: 'x', at: right - share * width, why } : null)
    }
    const onUp = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      setSizing(null)
      setGuide(null)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  /** The gutter slider settles on the usual widths as it passes them. */
  const chooseGutter = (value: number) => {
    const near = GUTTER_STOPS.find((stop) => Math.abs(stop - value) <= 2)
    setGutter(near ?? value)
  }

  /**
   * The corner button (2026-10-15): the next spare in its place, in the same
   * frame; pressed again, the one after that; past the last, new ones.
   */
  const replaceNext = (slot: number) => {
    // Forward through the spares; past the last of them, new pictures
    // rather than round again (a swapped-out picture is a spare too, and
    // going round would only toggle between the same two).
    const current = order[slot]
    const spare = spares.find((i) => i > current)
    if (spare === undefined) {
      void replaceFresh(slot)
      return
    }
    setFrozen((now) => now ?? order)
    setOrder((now) => now.map((i, n) => (n === slot ? spare : i)))
  }

  /** No spares left: a new picture from the caller (findMore), read, and put in its place. */
  const [finding, setFinding] = useState<number | null>(null)
  const replaceFresh = async (slot: number) => {
    if (!findMore || finding !== null) return
    const place = sheet.slots[slot]
    const was = pictures[order[slot]]
    if (!place || !was) return
    setFinding(slot)
    setError('')
    try {
      const src = await findMore({ shape: place.w / place.h, was, used: new Set(pictures.map((one) => one.src.key)) })
      const read = await readPicture(src)
      const index = pictures.length
      setPictures((now) => [...now, read])
      setFrozen((now) => now ?? order)
      setOrder((now) => now.map((i, n) => (n === slot ? index : i)))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A new picture could not be found')
    } finally {
      setFinding(null)
    }
  }
  const takeOff = (slot: number) => {
    relayout()
    setOrder((now) => now.filter((_, n) => n !== slot))
    setSelected(null)
  }

  // ---------------------------------------------------------- lifted pictures (2026-10-23)
  /**
   * Lift a picture off the grid: the grid lays itself out again without it,
   * and from then on the two have nothing to do with each other. It starts
   * whole, fitted in the frame it left, at the angle it was turned to there.
   */
  const lift = (n: number) => {
    const slot = sheet.slots[n]
    if (!slot || order.length < 2) return
    const pic = slot.pic
    const ratio = pictures[pic].ratio || 1
    const w = Math.min(slot.w, slot.h * ratio)
    const angle = normalAngle(turns[pic] ?? 0)
    setCropping(null)
    relayout()
    setOrder((now) => now.filter((i) => i !== pic))
    setTurns((now) => {
      const out = { ...now }
      delete out[pic]
      return out
    })
    setFloats((now) => [...now.filter((one) => one.pic !== pic), { pic, x: slot.x + slot.w / 2, y: slot.y + slot.h / 2, w, angle }])
    setSelected({ kind: 'float', pic })
  }
  /** A lifted picture changed, and kept on the sheet (keepOnSheet, 2026-10-23). */
  const changeFloat = (pic: number, change: (one: BoardFloat) => BoardFloat) =>
    setFloats((now) =>
      now.map((one) => {
        if (one.pic !== pic) return one
        const next = change(one)
        const spec: FloatSpec = {
          angle: next.angle,
          outline: next.outline ?? gutter,
          color: ground,
          w: next.w,
          h: next.w / (pictures[pic]?.ratio || 1),
        }
        return { ...next, ...keepOnSheet(next.x, next.y, spec, SHEET_W, SHEET_H) }
      }),
    )
  /** Back into the grid, at its end (where there is room). */
  const putBack = (pic: number) => {
    if (order.length >= MOST_PICTURES) return
    setFloats((now) => now.filter((one) => one.pic !== pic))
    relayout()
    setOrder((now) => [...now, pic])
    setSelected(null)
  }
  /** Off the sheet altogether: a spare again. */
  const dropFloat = (pic: number) => {
    setFloats((now) => now.filter((one) => one.pic !== pic))
    setSelected(null)
  }
  /** The lifted picture's angle while its handle is held. */
  const [floatTurning, setFloatTurning] = useState<number | null>(null)

  /**
   * A press on a lifted picture: it comes to the top and is selected, and
   * dragged it moves. Moving, sizing and turning all follow the pointer from
   * where it was pressed, in sheet units.
   */
  const startFloat = (event: React.PointerEvent<HTMLElement>, pic: number, how: 'move' | 'size' | 'turn') => {
    if (event.button !== 0 || !stage.current) return
    const start = floats.find((one) => one.pic === pic)
    if (!start) return
    event.preventDefault()
    event.stopPropagation()
    setSelected({ kind: 'float', pic })
    setFloats((now) => [...now.filter((one) => one.pic !== pic), ...now.filter((one) => one.pic === pic)])
    const rect = stage.current.getBoundingClientRect()
    const cx = rect.left + start.x * scale
    const cy = rect.top + start.y * scale
    const x0 = event.clientX
    const y0 = event.clientY
    const reach0 = Math.max(1, Math.hypot(x0 - cx, y0 - cy))
    const angleAt = (x: number, y: number) => (Math.atan2(y - cy, x - cx) * 180) / Math.PI
    const from = angleAt(x0, y0)
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)
    const onMove = (e: PointerEvent) => {
      if (how === 'move') {
        changeFloat(pic, (one) => ({ ...one, x: start.x + (e.clientX - x0) / scale, y: start.y + (e.clientY - y0) / scale }))
      } else if (how === 'size') {
        // About its middle: as far from it as the pointer now is, against where it was pressed.
        const factor = Math.hypot(e.clientX - cx, e.clientY - cy) / reach0
        changeFloat(pic, (one) => ({ ...one, w: Math.min(SHEET_W * 1.5, Math.max(40, start.w * factor)) }))
      } else {
        let angle = start.angle + angleAt(e.clientX, e.clientY) - from
        if (e.shiftKey) angle = Math.round(angle / 15) * 15
        else if (!e.altKey) {
          const quarter = Math.round(angle / 90) * 90
          if (Math.abs(angle - quarter) < 3) angle = quarter
        }
        angle = normalAngle(Math.round(angle * 10) / 10)
        changeFloat(pic, (one) => ({ ...one, angle }))
        setFloatTurning(angle)
      }
    }
    const onUp = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      setFloatTurning(null)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  // A selected lifted picture: Delete takes it off the sheet, Escape lets it go.
  useEffect(() => {
    if (selected?.kind !== 'float') return
    const pic = selected.pic
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        setFloats((now) => now.filter((one) => one.pic !== pic))
        setSelected(null)
      } else if (event.key === 'Escape') setSelected(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected])
  const shuffle = () => {
    relayout()
    setOrder((now) => {
      const all = [...now]
      for (let i = all.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[all[i], all[j]] = [all[j], all[i]]
      }
      return all
    })
  }
  /** Two pictures change frames; the frames stay as they are. */
  const swap = (a: number, b: number) => {
    if (a === b) return
    setFrozen((now) => now ?? order)
    setOrder((now) => now.map((i, n) => (n === a ? now[b] : n === b ? now[a] : i)))
    setSelected({ kind: 'picture', index: b })
  }
  /** A press that turned into a drag: the click after it does not select. */
  const moved = useRef(false)
  const slotUnder = (x: number, y: number): number | null => {
    const under = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-slot]')
    return under ? Number(under.dataset.slot) : null
  }

  /** Press and drag a picture onto another frame: the two change places. */
  const startDrag = (event: React.PointerEvent<HTMLButtonElement>, from: number) => {
    if (event.button !== 0) return
    moved.current = false
    const x0 = event.clientX
    const y0 = event.clientY
    const onMove = (e: PointerEvent) => {
      if (!moved.current && Math.hypot(e.clientX - x0, e.clientY - y0) < 6) return
      moved.current = true
      setDrag({ from, over: slotUnder(e.clientX, e.clientY), x: e.clientX, y: e.clientY })
    }
    const onUp = (e: PointerEvent) => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (!moved.current) return
      const to = slotUnder(e.clientX, e.clientY)
      if (to !== null) swap(from, to)
      setDrag(null)
      window.setTimeout(() => {
        moved.current = false
      }, 0)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  /** Re-cutting: drag the picture about inside its frame. */
  const startRecut = (event: React.PointerEvent<HTMLButtonElement>, n: number) => {
    if (event.button !== 0 || !sheet) return
    event.preventDefault()
    moved.current = false
    const slot = sheet.slots[n]
    const slackX = (slot.w / slot.crop.w - slot.w) * scale
    const slackY = (slot.h / slot.crop.h - slot.h) * scale
    const from = {
      x: slot.crop.w < 1 ? slot.crop.x / (1 - slot.crop.w) : 0.5,
      y: slot.crop.h < 1 ? slot.crop.y / (1 - slot.crop.h) : 0.5,
    }
    const x0 = event.clientX
    const y0 = event.clientY
    const clamp = (v: number) => Math.min(1, Math.max(0, v))
    const onMove = (e: PointerEvent) => {
      moved.current = true
      const x = slackX > 0.5 ? clamp(from.x - (e.clientX - x0) / slackX) : from.x
      const y = slackY > 0.5 ? clamp(from.y - (e.clientY - y0) / slackY) : from.y
      setFocus((now) => ({ ...now, [slot.pic]: { x, y, z: now[slot.pic]?.z } }))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.setTimeout(() => {
        moved.current = false
      }, 0)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  /**
   * A corner of the picture dragged while re-cutting (Huanrui, 2026-10-15):
   * the picture grows or shrinks about the opposite corner, keeping its
   * shape, never smaller than filling its frame and never more than 6 times
   * that, and always covering the frame.
   */
  const startCorner = (event: React.PointerEvent<HTMLElement>, n: number, corner: Corner) => {
    if (event.button !== 0 || !sheet || !stage.current) return
    event.preventDefault()
    event.stopPropagation()
    const slot = sheet.slots[n]
    const pic = slot.pic
    const ratio = shapeOf(pic)
    const base = coverCrop(ratio, slot.w, slot.h)
    const smallest = slot.w / base.w
    const fullW = slot.w / slot.crop.w
    const fullH = slot.h / slot.crop.h
    const gx0 = slot.x - slot.crop.x * fullW
    const gy0 = slot.y - slot.crop.y * fullH
    const west = corner.endsWith('w')
    const north = corner.startsWith('n')
    // The opposite corner stays where it is.
    const ax = west ? gx0 + fullW : gx0
    const ay = north ? gy0 + fullH : gy0
    const rect = stage.current.getBoundingClientRect()
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)
    const onMove = (e: PointerEvent) => {
      const px = (e.clientX - rect.left) / scale
      const py = (e.clientY - rect.top) / scale
      const w = Math.min(smallest * 6, Math.max(smallest, Math.abs(px - ax), Math.abs(py - ay) * ratio))
      const h = w / ratio
      let gx = west ? ax - w : ax
      let gy = north ? ay - h : ay
      // Still covering the frame.
      gx = Math.min(slot.x, Math.max(slot.x + slot.w - w, gx))
      gy = Math.min(slot.y, Math.max(slot.y + slot.h - h, gy))
      const cw = slot.w / w
      const ch = slot.h / h
      const cx = (slot.x - gx) / w
      const cy = (slot.y - gy) / h
      setFocus((now) => ({
        ...now,
        [pic]: { x: cw < 1 ? cx / (1 - cw) : 0.5, y: ch < 1 ? cy / (1 - ch) : 0.42, z: w / smallest },
      }))
    }
    const onUp = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  /**
   * Bigger or smaller in its frame while re-cutting (Huanrui, 2026-10-15):
   * by `factor`, keeping the point of the picture at (u, v) -- fractions of
   * the frame, the pointer for the wheel, the middle for the buttons -- where
   * it is. Never smaller than filling the frame, never more than 6 times.
   */
  const zoomBy = useCallback(
    (n: number, factor: number, u = 0.5, v = 0.5) => {
      if (!sheet) return
      const slot = sheet.slots[n]
      if (!slot) return
      const pic = slot.pic
      const base = coverCrop(shapeOf(pic), slot.w, slot.h)
      const zNow = Math.max(1, focus[pic]?.z ?? 1)
      const z = Math.min(6, Math.max(1, zNow * factor))
      // The point of the picture under (u, v) now...
      const px = slot.crop.x + u * slot.crop.w
      const py = slot.crop.y + v * slot.crop.h
      // ...stays under it at the new size.
      const w = base.w / z
      const h = base.h / z
      const cx = Math.min(1 - w, Math.max(0, px - u * w))
      const cy = Math.min(1 - h, Math.max(0, py - v * h))
      setFocus((now) => ({
        ...now,
        [pic]: { x: w < 1 ? cx / (1 - w) : 0.5, y: h < 1 ? cy / (1 - h) : 0.42, z },
      }))
    },
    // shapeOf reads turns and pictures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focus, pictures, sheet, turns],
  )

  // The wheel over the picture being re-cut zooms it, not the page.
  useEffect(() => {
    if (cropping === null) return
    const node = stage.current?.querySelector<HTMLElement>(`[data-slot="${cropping}"]`)
    if (!node) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const r = node.getBoundingClientRect()
      zoomBy(cropping, Math.exp(-event.deltaY * 0.0015), (event.clientX - r.left) / r.width, (event.clientY - r.top) / r.height)
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [cropping, zoomBy])

  // A clicked picture comes off the sheet with Delete or Backspace (2026-10-15:
  // the picture panel, and its "Take off the board", is gone).
  useEffect(() => {
    if (selected?.kind !== 'picture' || cropping !== null) return
    const index = selected.index
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return
      if ((event.key === 'Delete' || event.key === 'Backspace') && order.length > 1) {
        event.preventDefault()
        takeOff(index)
      } else if (event.key === 'Escape') setSelected(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // takeOff is remade each render; what matters is which picture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, cropping, order.length])

  // Re-cutting ends with Enter or Escape, or a press anywhere else.
  useEffect(() => {
    if (cropping === null) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter') setCropping(null)
    }
    const onDown = (event: PointerEvent) => {
      const inside = (event.target as Element | null)?.closest?.(`[data-slot="${cropping}"], .mbd__handle`)
      if (!inside) setCropping(null)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onDown, true)
    }
  }, [cropping])


  // ---------------------------------------------------------- drawing
  const px = (v: number) => `${v * scale}px`
  const box = (r: Rect) => ({ left: px(r.x), top: px(r.y), width: px(r.w), height: px(r.h) })
  const textCss = (style: TextStyle) => ({
    fontFamily: miroFont(style.font).css,
    fontSize: px(style.size),
    fontWeight: style.bold ? 700 : 400,
    textAlign: style.align,
    color: style.color,
  })

  const textStyle = selected?.kind === 'text' ? (selected.id === 'title' ? titleStyle : wordsStyle) : null
  const setTextStyle = selected?.kind === 'text' ? (selected.id === 'title' ? setTitleStyle : setWordsStyle) : null

  return (
    <section className={`mbd${className ? ` ${className}` : ''}`}>
      <div className="mbd__main">
        <div
          className={`mbd__sheet${cropping !== null ? ' mbd__sheet--recut' : ''}`}
          ref={stage}
          style={{
            background: ground,
            ...(sheetWidth ? { width: sheetWidth, height: (sheetWidth * 9) / 16, marginInline: 'auto' } : {}),
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) setSelected(null)
          }}
        >
          {sheet.slots.map((slot, n) => {
            const src = pictures[slot.pic].src
            const recut = cropping === n
            const classes = [
              'mbd__pic',
              selected?.kind === 'picture' && selected.index === n ? 'mbd__pic--on' : '',
              recut ? 'mbd__pic--recut' : '',
              drag && drag.from === n ? 'mbd__pic--lifted' : '',
              drag && drag.over === n && drag.from !== n ? 'mbd__pic--target' : '',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <button
                type="button"
                key={`${slot.pic}-${n}`}
                data-slot={n}
                className={classes}
                style={box(slot)}
                onPointerDown={(event) => (recut ? startRecut(event, n) : startDrag(event, n))}
                onClick={() => {
                  if (!moved.current) setSelected({ kind: 'picture', index: n })
                }}
                onDoubleClick={() => {
                  setSelected({ kind: 'picture', index: n })
                  setCropping(n)
                }}
                aria-label={`${src.title}: drag to another frame, double-click to re-cut`}
                title={recut ? 'Drag to move it in its frame · drag a corner (or the wheel) to size it · Enter or click outside when done' : undefined}
              >
                {/* Placed by its crop, so a picture zoomed in its frame shows as it will be cut. */}
                <span className="mbd__clip">
                  <TurnedImage
                    src={sharpEnough(pictures[slot.pic], slot, scale, recut)}
                    alt={src.title}
                    turn={slot.turn ?? 0}
                    box={{
                      x: (-slot.crop.x / slot.crop.w) * slot.w * scale,
                      y: (-slot.crop.y / slot.crop.h) * slot.h * scale,
                      w: (slot.w / slot.crop.w) * scale,
                      h: (slot.h / slot.crop.h) * scale,
                    }}
                  />
                </span>
                {/* The next spare in its place (2026-10-15). */}
                {findMore && !recut && !drag ? (
                  <span
                    role="button"
                    tabIndex={-1}
                    className={`mbd__swap${finding === n ? ' mbd__swap--busy' : ''}`}
                    title="Swap for another picture: the next spare, or a new one like these"
                    aria-label="Swap for the next spare picture"
                    onPointerDown={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation()
                      replaceNext(n)
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M3 6.5a5 5 0 0 1 9-2.2M13 9.5a5 5 0 0 1-9 2.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M12.5 1.8v3h-3M3.5 14.2v-3h3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              </button>
            )
          })}
          {/* Re-cutting: the whole picture shows faintly round its frame. */}
          {cropping !== null && sheet.slots[cropping]
            ? (() => {
                const slot = sheet.slots[cropping]
                const fullW = slot.w / slot.crop.w
                const fullH = slot.h / slot.crop.h
                const ghost = { x: slot.x - slot.crop.x * fullW, y: slot.y - slot.crop.y * fullH, w: fullW, h: fullH }
                const corners: Corner[] = ['nw', 'ne', 'sw', 'se']
                return (
                  <>
                    <span className="mbd__ghost" style={box(ghost)}>
                      <TurnedImage
                        src={pictures[slot.pic].src.url}
                        alt=""
                        turn={slot.turn ?? 0}
                        box={{ x: 0, y: 0, w: ghost.w * scale, h: ghost.h * scale }}
                      />
                    </span>
                    {/* Everything but the frame darkened (Huanrui, 2026-10-15). */}
                    <div className="mbd__dim" aria-hidden="true">
                      <div className="mbd__hole" style={box(slot)} />
                    </div>
                    {/* The picture's four corners: drag one to make it bigger or smaller. */}
                    {corners.map((corner) => (
                      <span
                        key={corner}
                        className={`mbd__handle mbd__handle--${corner}`}
                        style={{
                          left: px(corner.endsWith('w') ? ghost.x : ghost.x + ghost.w),
                          top: px(corner.startsWith('n') ? ghost.y : ghost.y + ghost.h),
                        }}
                        onPointerDown={(event) => startCorner(event, cropping, corner)}
                      />
                    ))}
                    {/* Lift it out (2026-10-23): at the frame's other top corner, the turn
                        handle's mirror. Not the last picture in the grid. */}
                    {order.length > 1 ? (
                      <span className="mbd__lifts mbd__handle" style={{ left: px(slot.x), top: px(slot.y) }}>
                        <button
                          type="button"
                          title="Lift it out: whole, over the sheet, outlined in the ground's colour"
                          aria-label="Lift it out"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation()
                            lift(cropping)
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <rect x="2.5" y="5.5" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M8 2.5h5.5V8M13.5 2.5 8.5 7.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </span>
                    ) : null}
                    {/* Turn it to any angle in its frame (2026-10-16): one handle. */}
                    <span className="mbd__turns mbd__handle" style={{ left: px(slot.x + slot.w), top: px(slot.y) }}>
                      <button
                        type="button"
                        title="Drag round to turn it to any angle · click for a quarter turn · Shift: 15° steps · Alt: no settling"
                        aria-label="Turn"
                        onPointerDown={(event) => startTurn(event, cropping)}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M12 6.5A5 5 0 1 0 12.5 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          <path d="M14.2 3.8 12 6.5 9.2 4.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {turning !== null || (turns[slot.pic] ?? 0) !== 0 ? (
                        <em className="mbd__angle">{`${turning ?? normalAngle(turns[slot.pic])}°`}</em>
                      ) : null}
                    </span>
                  </>
                )
              })()
            : null}
          {/* The gutters, to drag (2026-10-16): not while a picture is being re-cut or carried. */}
          {cropping === null && !drag
            ? sheet.dividers.map((d) => {
                const on = sizing?.path === d.path && sizing.index === d.index
                const mid = (d.at + d.width / 2) * scale
                const style =
                  d.axis === 'x'
                    ? { left: `${mid - 6}px`, top: px(d.from), width: '12px', height: px(d.to - d.from) }
                    : { top: `${mid - 6}px`, left: px(d.from), height: '12px', width: px(d.to - d.from) }
                return (
                  <span
                    key={`${d.path}:${d.index}`}
                    className={`mbd__gutter mbd__gutter--${d.axis}${on ? ' mbd__gutter--on' : ''}`}
                    style={style}
                    onPointerDown={(event) => startDivider(event, d)}
                    title="Drag to size the frames either side · Alt: no snapping"
                  />
                )
              })
            : null}
          {/* Half: the pictures' left edge, to drag (2026-10-16). */}
          {kind === 'half' && cropping === null && !drag && sheet.slots.length > 0
            ? (() => {
                const left = Math.min(...sheet.slots.map((one) => one.x))
                const top = Math.min(...sheet.slots.map((one) => one.y))
                const bottom = Math.max(...sheet.slots.map((one) => one.y + one.h))
                return (
                  <span
                    className={`mbd__gutter mbd__gutter--x mbd__edge${sizing?.path === 'half' ? ' mbd__gutter--on' : ''}`}
                    style={{ left: `${left * scale - 9}px`, top: px(top), width: '12px', height: px(bottom - top) }}
                    onPointerDown={startHalfEdge}
                    title="Drag to give the pictures more or less of the sheet · Alt: no snapping"
                  />
                )
              })()
            : null}
          {guide ? (
            <span
              className={`mbd__guide mbd__guide--${guide.axis}`}
              style={guide.axis === 'x' ? { left: `${guide.at * scale}px` } : { top: `${guide.at * scale}px` }}
            >
              <em>
                {guide.why === 'equal'
                  ? 'Even'
                  : guide.why === 'fit'
                    ? 'Whole picture'
                    : guide.why === 'centre'
                      ? 'Middle'
                      : guide.why === 'line'
                        ? 'Lined up'
                        : guide.why}
              </em>
            </span>
          ) : null}
          {sheet.parts ? (
            <>
              <button
                type="button"
                className={`mbd__text${selected?.kind === 'text' && selected.id === 'title' ? ' mbd__text--on' : ''}`}
                style={{ ...box(sheet.parts.title), ...textCss(titleStyle) }}
                onClick={() => setSelected({ kind: 'text', id: 'title' })}
              >
                {title || <em>Title</em>}
              </button>
              <button
                type="button"
                className={`mbd__text${selected?.kind === 'text' && selected.id === 'keywords' ? ' mbd__text--on' : ''}`}
                style={{ ...box(sheet.parts.keywords), ...textCss(wordsStyle) }}
                onClick={() => setSelected({ kind: 'text', id: 'keywords' })}
              >
                {keywords || <em>Words</em>}
              </button>
              {sheet.parts.swatches.map((rect, i) => (
                <span key={i} className="mbd__swatch" style={{ ...box(rect), background: palette[i] }} title={palette[i]} />
              ))}
            </>
          ) : null}
          {/* Lifted pictures (2026-10-23): over everything, the last on top;
              moved by dragging, sized by a corner, turned by the handle above. */}
          {sheet.floats.map((one) => {
            const t = one.spec.outline
            const on = selected?.kind === 'float' && selected.pic === one.pic
            const src = pictures[one.pic].src
            return (
              <div
                key={`float-${one.pic}`}
                className={`mbd__float${on ? ' mbd__float--on' : ''}`}
                style={{
                  left: px(one.x - one.spec.w / 2 - t),
                  top: px(one.y - one.spec.h / 2 - t),
                  width: px(one.spec.w + 2 * t),
                  height: px(one.spec.h + 2 * t),
                  transform: `rotate(${one.spec.angle}deg)`,
                }}
                role="button"
                tabIndex={-1}
                aria-label={`${src.title}: drag to move it`}
                onPointerDown={(event) => startFloat(event, one.pic, 'move')}
              >
                <FloatView url={src.url} spec={one.spec} scale={scale} />
                {on ? (
                  <>
                    {(['nw', 'ne', 'sw', 'se'] as Corner[]).map((corner) => (
                      <span
                        key={corner}
                        className={`mbd__fsize mbd__fsize--${corner}`}
                        title="Drag to size it"
                        onPointerDown={(event) => startFloat(event, one.pic, 'size')}
                      />
                    ))}
                    <span
                      className="mbd__fturn"
                      title="Drag round to turn it · Shift: 15° steps · Alt: no settling"
                      onPointerDown={(event) => startFloat(event, one.pic, 'turn')}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M12 6.5A5 5 0 1 0 12.5 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M14.2 3.8 12 6.5 9.2 4.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {floatTurning !== null || one.spec.angle !== 0 ? <em className="mbd__angle">{`${floatTurning ?? one.spec.angle}°`}</em> : null}
                    </span>
                  </>
                ) : null}
              </div>
            )
          })}
        </div>
        {/* The photographers' credit, under the sheet rather than in its margin
            (2026-10-23): the margin can now be brought in to nothing. */}
        {sheet.credit ? (
          <p className="mbd__credit-out">
            <CreditLinks
              sources={[...sheet.slots.map((slot) => slot.pic), ...sheet.floats.map((one) => one.pic)].map((i) => pictures[i].src)}
              fallback={sheet.credit}
            />
          </p>
        ) : null}
        {drag ? (
          <img
            className="mbd__carry"
            src={pictures[sheet.slots[drag.from].pic].src.thumb}
            alt=""
            style={{ left: drag.x, top: drag.y }}
          />
        ) : null}
        <p className="mbd__hint">
          {order.length} of {pictures.length} pictures · drag a picture to swap frames · drag the gaps between them to size the frames · double-click to move it in its frame, drag its corners to size it · Delete takes a clicked one off · click a text to change it{hint ? ' · ' : ''}
{hint}
        </p>
      </div>

      <aside className="mbd__side">
        {/* The picture panel is gone (Huanrui, 2026-10-15): pictures are
            swapped by dragging, replaced from their corner, taken off with
            Delete. A clicked picture leaves the layout panel showing. */}
        {textStyle && setTextStyle && selected?.kind === 'text' ? (
          <div className="mbd__panel">
            <div className="mbd__label">{selected.id === 'title' ? 'Title' : 'Words'}</div>
            <textarea
              className="mbd__input"
              rows={2}
              value={selected.id === 'title' ? title : keywords}
              onChange={(event) => (selected.id === 'title' ? setTitle : setKeywords)(event.target.value)}
              autoFocus
            />
            <TypeControls style={textStyle} onChange={setTextStyle} />
            {/* The way out of the text, plain to see (2026-10-23): it was a quiet
                outline that read as one more option. */}
            <button type="button" className="mbd__btn mbd__btn--done" onClick={() => setSelected(null)}>
              Done
            </button>
          </div>
        ) : selected?.kind === 'float' ? (
          <div className="mbd__panel">
            <div className="mbd__label">Lifted picture</div>
            <p className="mbd__quiet">
              Drag it to move it, a corner to size it, the round handle to turn it. Its outline is the ground's colour.
            </p>
            {/* The outline's width (Huanrui, 2026-10-23): as wide as the gutters,
                following them, or a width of its own -- moving the slider
                chooses one; ticking the box goes back to the gutters. */}
            {(() => {
              const one = floats.find((f) => f.pic === selected.pic)
              const own = typeof one?.outline === 'number'
              const width = own ? (one?.outline as number) : gutter
              return (
                <>
                  <div className="mbd__label">
                    Outline <span className="mbd__value">{width}</span>
                  </div>
                  <input
                    className="mbd__range"
                    type="range"
                    min={0}
                    max={GUTTER_MAX}
                    step={1}
                    value={width}
                    onChange={(event) => changeFloat(selected.pic, (f) => ({ ...f, outline: Number(event.target.value) }))}
                    aria-label="Outline width"
                  />
                  <label className="mbd__check">
                    <input
                      type="checkbox"
                      checked={!own}
                      onChange={(event) =>
                        changeFloat(selected.pic, (f) => {
                          if (event.target.checked) {
                            const { outline: _gone, ...rest } = f
                            return rest
                          }
                          return { ...f, outline: gutter }
                        })
                      }
                    />
                    Same as the gutters
                  </label>
                </>
              )
            })()}
            <div className="mbd__row">
              <button
                type="button"
                className="mbd__btn"
                disabled={order.length >= MOST_PICTURES}
                onClick={() => putBack(selected.pic)}
                title="Back into the grid, at its end"
              >
                Put back
              </button>
              <button type="button" className="mbd__btn" onClick={() => dropFloat(selected.pic)} title="Off the sheet (Delete)">
                Remove
              </button>
            </div>
            <button type="button" className="mbd__btn mbd__btn--done" onClick={() => setSelected(null)}>
              Done
            </button>
          </div>
        ) : (
          <div className="mbd__panel">
            <div className="mbd__label">Layout</div>
            <div className="mbd__layouts">
              {LAYOUTS.map((layout) => (
                <button
                  type="button"
                  key={layout.kind}
                  className={`mbd__layout${kind === layout.kind ? ' on' : ''}`}
                  onClick={() => {
                    if (layout.kind !== kind) {
                      setSplits({})
                      setHalf(null)
                    }
                    setKind(layout.kind)
                  }}
                  title={layout.hint}
                >
                  <LayoutIcon kind={layout.kind} />
                  <span>{layout.label}</span>
                </button>
              ))}
            </div>
            <div className="mbd__row">
              <button type="button" className="mbd__btn" onClick={shuffle} title="Mix them up">
                Shuffle
              </button>
            </div>

            <div className="mbd__label">
              Gutters <span className="mbd__value">{gutter}</span>
            </div>
            <input
              className="mbd__range"
              type="range"
              min={0}
              max={GUTTER_MAX}
              step={1}
              value={gutter}
              onChange={(event) => chooseGutter(Number(event.target.value))}
              aria-label="Gutter width"
            />
            {/* No "Reset frame sizes" button (Huanrui, 2026-10-23): another
                layout, or Shuffle, lays the frames out afresh. */}

            {/* The pictures' distance from the sheet's edges (2026-10-23). Up to
                the usual margin, never past it: the title strip and colours
                stay where they are, and the pictures never sit inside them. */}
            <div className="mbd__label">
              Margin <span className="mbd__value">{margin}</span>
            </div>
            <input
              className="mbd__range"
              type="range"
              min={0}
              max={MARGIN}
              step={1}
              value={margin}
              onChange={(event) => setMargin(Number(event.target.value))}
              aria-label="Distance from the pictures to the sheet's edges"
            />

            <div className="mbd__label">Ground</div>
            <div className="mbd__grounds">
              {GROUNDS.map((one) => (
                <button
                  type="button"
                  key={one.color}
                  className={`mbd__ground${ground === one.color ? ' on' : ''}`}
                  onClick={() => setGroundAndText(one.color)}
                  title={one.label}
                >
                  <span style={{ background: one.color }} />
                  {one.label}
                </button>
              ))}
            </div>

            <label className="mbd__check">
              <input type="checkbox" checked={foot} onChange={(event) => setFoot(event.target.checked)} />
              Title and words
            </label>
            {foot ? (
              <div className="mbd__footset">
                <div className="mbd__seg" role="radiogroup" aria-label="Where the title and words go">
                  {(['top', 'bottom'] as const).map((at) => (
                    <button
                      type="button"
                      key={at}
                      role="radio"
                      aria-checked={footAt === at}
                      className={footAt === at ? 'on' : ''}
                      onClick={() => {
                        if (at !== footAt) setSplits({})
                        setFootAt(at)
                      }}
                    >
                      {at === 'top' ? 'Top' : 'Bottom'}
                    </button>
                  ))}
                </div>
                <label className="mbd__mini">
                  Height <span className="mbd__value">{footH}</span>
                  <input
                    className="mbd__range"
                    type="range"
                    min={FOOT_MIN}
                    max={FOOT_MAX}
                    step={1}
                    value={footH}
                    onChange={(event) => {
                      const v = Number(event.target.value)
                      setFootH(Math.abs(v - FOOT) <= 3 ? FOOT : v)
                    }}
                    aria-label="Height of the title strip"
                  />
                </label>
              </div>
            ) : null}
            <label className="mbd__check">
              <input
                type="checkbox"
                checked={showColours}
                disabled={!foot || palette.length === 0}
                onChange={(event) => setShowColours(event.target.checked)}
              />
              Colour strip
            </label>
          </div>
        )}

        <div className="mbd__foot">
          {error ? <p className="mbd__error">{error}</p> : null}
          {footer({ pictures, settings, sheet, board: () => buildBoard(pictures, sheet, settings, palette) })}
        </div>
      </aside>
    </section>
  )
}

function TypeControls({ style, onChange }: { style: TextStyle; onChange: (style: TextStyle) => void }) {
  const set = <K extends keyof TextStyle>(key: K, value: TextStyle[K]) => onChange({ ...style, [key]: value })
  useEffect(() => loadMiroFont(style.font), [style.font])
  return (
    <div className="mbd__type">
      <select
        className="mbd__select"
            // Each name in its own face: every typeface is fetched when the list is first reached for.
            onPointerEnter={loadAllMiroFonts}
            onFocus={loadAllMiroFonts}
        value={style.font}
        onChange={(event) => set('font', event.target.value as MiroFontKey)}
        style={{ fontFamily: miroFont(style.font).css }}
        aria-label="Typeface"
      >
        {MIRO_FONTS.map((font) => (
          <option key={font.key} value={font.key} style={{ fontFamily: font.css }}>
            {font.label}
          </option>
        ))}
      </select>
      <div className="mbd__row">
        <input
          className="mbd__num"
          type="number"
          min={8}
          max={160}
          value={style.size}
          onChange={(event) => set('size', Math.max(8, Math.min(160, Number(event.target.value) || style.size)))}
          aria-label="Size"
        />
        <button type="button" className={`mbd__tog${style.bold ? ' on' : ''}`} onClick={() => set('bold', !style.bold)} aria-label="Bold">
          <b>B</b>
        </button>
        {(['left', 'center', 'right'] as const).map((align) => (
          <button
            type="button"
            key={align}
            className={`mbd__tog${style.align === align ? ' on' : ''}`}
            onClick={() => set('align', align)}
            aria-label={`Align ${align}`}
          >
            <AlignIcon align={align} />
          </button>
        ))}
        <input
          className="mbd__colour"
          type="color"
          value={style.color}
          onChange={(event) => set('color', event.target.value)}
          aria-label="Colour"
        />
      </div>
    </div>
  )
}

function AlignIcon({ align }: { align: 'left' | 'center' | 'right' }) {
  const x = (w: number) => (align === 'left' ? 2 : align === 'right' ? 14 - w : (16 - w) / 2)
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      {[12, 8, 10].map((w, i) => (
        <rect key={i} x={x(w)} y={3 + i * 4} width={w} height="1.6" rx="0.8" fill="currentColor" />
      ))}
    </svg>
  )
}

/** A thumbnail of each layout, drawn by the layout itself with grey boxes. */
function LayoutIcon({ kind }: { kind: LayoutKind }) {
  const ratios = [0.8, 1.3, 0.75, 1, 1.5, 0.9, 1.2]
  const rects = placePictures(kind, { ratios, foot: true })
  const s = 64 / SHEET_W
  return (
    <svg width="64" height={SHEET_H * s} viewBox={`0 0 ${SHEET_W} ${SHEET_H}`} aria-hidden="true">
      <rect width={SHEET_W} height={SHEET_H} rx="40" fill="currentColor" opacity="0.08" />
      {rects.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} rx="10" fill="currentColor" opacity={i === 0 ? 0.75 : 0.4} />
      ))}
    </svg>
  )
}

/**
 * A picture drawn into the box its crop gives it -- the part shown fills the
 * frame -- turned to any angle (2026-10-16; turn.ts). `box` is the picture
 * turned and straightened; the image itself is that much larger, drawn the
 * other way round when it is turned sideways, and rotated into it.
 */
function TurnedImage({ src, alt, turn, box }: { src: string; alt: string; turn: number; box: Rect }) {
  const { q, rest } = splitTurn(turn)
  const sideways = q % 2 === 1
  const keep = keptShare(box.w, box.h, rest)
  const w = (sideways ? box.h : box.w) / keep
  const h = (sideways ? box.w : box.h) / keep
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      style={{
        position: 'absolute',
        left: `${box.x + (box.w - w) / 2}px`,
        top: `${box.y + (box.h - h) / 2}px`,
        width: `${w}px`,
        height: `${h}px`,
        maxWidth: 'none',
        maxHeight: 'none',
        ...(q || rest ? { transform: `rotate(${q * 90 + rest}deg)` } : {}),
      }}
    />
  )
}

/**
 * The copy sharp enough for how large it is drawn (Huanrui, 2026-10-16): the
 * pixels the picture covers on this screen, high-density screens included,
 * against what each copy has. The thumbnail (about 600 px) while it is enough;
 * then the picture's own file (a library or board picture's original, an
 * Unsplash or Pexels picture's web size); then, for those two, the full size.
 * As sharp as the original wherever it shows, without every picture on the
 * sheet decoded at four thousand pixels.
 */
function sharpEnough(picture: BoardPicture, slot: SheetSlot, scale: number, recut: boolean): string {
  const src = picture.src
  // A translucent ground: its solid copy, at every size (2026-10-23).
  if (picture.clear === 'semi') return src.url
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  // The whole picture's longer side as drawn, in screen pixels.
  const need = Math.max(slot.w / slot.crop.w, slot.h / slot.crop.h) * scale * dpr
  if (src.full && (need > 1600 || recut)) return src.full
  // Never the thumbnail of a cut-out (2026-10-23): thumbnails were flattened
  // onto black or white when they were made.
  if (need > 560 || recut || picture.clear === 'cutout') return src.url
  return src.thumb
}

/**
 * Could the picture have see-through parts? Its own file's kind says: PNG,
 * WebP and GIF can, JPEG cannot -- by the library's record, or else by the
 * file's name in its address; a kind not known may.
 */
function mayBeClear(src: SourcePicture): boolean {
  const mime = src.asset?.mimeType ?? ''
  if (mime) return /png|webp|gif/i.test(mime)
  const path = (src.full || src.url).split(/[?#]/)[0]
  if (/\.(jpe?g)$/i.test(path)) return false
  // PNG, WebP, GIF -- or no kind to be read from the address (a picture read
  // from a Miro board): looked at.
  return true
}

/**
 * A lifted picture on screen (2026-10-23): drawn by float.ts -- the same
 * drawing that goes to a project's board and to Miro -- at the screen's own
 * resolution, so what is seen is what is sent. Drawn again only when its
 * size, outline or colour change; moving and turning it only move the
 * drawing.
 */
function FloatView({ url, spec, scale }: { url: string; spec: FloatSpec; scale: number }) {
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const [art, setArt] = useState<{ image: ImageBitmap; cutOut: boolean } | null>(null)
  useEffect(() => {
    let gone = false
    floatArt(url)
      .then((found) => {
        if (!gone) setArt(found)
      })
      .catch((error: unknown) => console.warn('[WonderWorks] could not draw the lifted picture', error))
    return () => {
      gone = true
    }
  }, [url])
  useEffect(() => {
    const node = canvas.current
    if (!node || !art) return
    const frame = window.requestAnimationFrame(() => {
      const drawn = drawOutlined(art.image, spec, scale * (window.devicePixelRatio || 1), art.cutOut)
      node.width = drawn.width
      node.height = drawn.height
      node.getContext('2d')?.drawImage(drawn, 0, 0)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [art, spec.w, spec.h, spec.outline, spec.color, scale])
  return <canvas ref={canvas} className="mbd__float-art" aria-hidden="true" />
}

/** Each picture read once for drawing lifted, with whether it has see-through parts. */
const floatArts = new Map<string, Promise<{ image: ImageBitmap; cutOut: boolean }>>()
function floatArt(url: string): Promise<{ image: ImageBitmap; cutOut: boolean }> {
  let found = floatArts.get(url)
  if (!found) {
    found = bitmapOf(url).then((image) => ({ image, cutOut: alphaKind(image) === 'cutout' }))
    found.catch(() => floatArts.delete(url))
    floatArts.set(url, found)
  }
  return found
}
